import { Component, DestroyRef, ElementRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MAT_TOOLTIP_DEFAULT_OPTIONS, MatTooltipModule } from '@angular/material/tooltip';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import {
  __,
  AlertComponent,
  applyStateToColDef,
  birdzTableFilterDateRange,
  birdzTableFilterRange,
  birdzTableFilterSelect,
  birdzTableFilterText,
  CardComponent,
  ColumnsChangedEvent,
  DisabledGranularity,
  FileService,
  FilterPipe,
  FormatterService,
  getPage,
  getSort,
  Initiator,
  isTableFavorite,
  MessengerService,
  Option,
  orange,
  Period,
  PeriodDisplayComponent,
  PeriodPickerComponent,
  PeriodPrevNextComponent,
  PeriodService,
  RoutingService,
  SelectSearchBarComponent,
  Str,
  TableFavorite,
  TableService,
  TableToolboxComponent,
  TimeShortcut,
  TimeUnit,
  valueFormatter,
  valueGetter,
} from '@web/birdz-angular';
import { AgGridAngular } from 'ag-grid-angular';
import {
  CellClassParams,
  CellClickedEvent,
  CellRendererSelectorResult,
  ColDef,
  Column,
  ColumnState,
  GridApi,
  GridOptions,
  GridReadyEvent,
  ICellRendererParams,
  IGetRowsParams,
  ITooltipParams,
  ValueFormatterParams,
} from 'ag-grid-community';
import { CellConnectionComponent } from 'app/components/cell-connection/cell-connection.component';
import { CellKpiNumberComponent } from 'app/components/cell-kpi-number/cell-kpi-number.component';
import { CellKpiStateComponent } from 'app/components/cell-kpi-state/cell-kpi-state.component';
import { CellStateComponent } from 'app/components/cell-state/cell-state.component';
import { ColumnHelpKpiComponent } from 'app/components/column-help-kpi/column-help-kpi.component';
import { ColumnHelpStateComponent } from 'app/components/column-help-state/column-help-state.component';
import { ModalHelpContractConfigComponent } from 'app/components/modal-help-contract-config/modal-help-contract-config.component';
import { ModalKpiPdsHistoryComponent } from 'app/components/modal-kpi-pds-history/modal-kpi-pds-history.component';
import { hasNumericValues, isKpiDisplayableForGranularity, Kpi } from 'app/interfaces/kpi.interface';
import { PdsFilterModel } from 'app/interfaces/pds-filter-model.interface';
import { kpiFilters, PdsExportRequest, PdsRequest } from 'app/interfaces/pds-request.interface';
import { Api } from 'app/services/api.service';
import { FilterService } from 'app/services/filter.service';
import { KpiPeriodService } from 'app/services/kpi-period.service';
import { UserService } from 'app/services/user.service';
import { KpiValue } from 'app/types/kpi-value.type';
import { format } from 'date-fns';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  distinctUntilChanged,
  EMPTY,
  filter,
  finalize,
  Observable,
  Subject,
  switchMap,
  takeUntil,
  tap,
  throwError,
} from 'rxjs';

// pseudo-KPIs showing the ongoing interruption (in days), displayed alongside YTD NB_JOURS KPIs
const INTERRUPTION_KPIS: Record<string, string[]> = {
  sewerin: ['INTERRUPTION_IN_PROGRESS'],
  kapta: ['NO_MEASURE_IN_PROGRESS', 'NO_COHERENT_MEASURE_IN_PROGRESS'],
};

@Component({
  selector: 'app-card-kpi-details',
  providers: [
    {
      provide: MAT_TOOLTIP_DEFAULT_OPTIONS,
      useValue: {
        showDelay: 300,
      },
    },
    { provide: PeriodService, useExisting: KpiPeriodService },
  ],
  imports: [
    MatButtonModule,
    MatTableModule,
    MatSortModule,
    MatTooltipModule,
    TranslocoPipe,
    CardComponent,
    FilterPipe,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    PeriodPickerComponent,
    PeriodPrevNextComponent,
    PeriodDisplayComponent,
    MatSelectModule,
    FormsModule,
    SelectSearchBarComponent,
    TableToolboxComponent,
    AgGridAngular,
    AlertComponent,
    ReactiveFormsModule,
    MatProgressSpinner,
  ],
  templateUrl: './card-kpi-details.component.html',
  styleUrl: './card-kpi-details.component.scss',
})
export class CardKpiDetailsComponent implements OnInit {
  private api = inject(Api);
  private filterService = inject(FilterService);
  private table = inject(TableService);
  private transloco = inject(TranslocoService);
  private messenger = inject(MessengerService);
  private userService = inject(UserService);
  private periodService = inject(KpiPeriodService);
  private formatter = inject(FormatterService);
  private dialog = inject(MatDialog);
  private routing = inject(RoutingService);
  private ref = inject(ElementRef);
  private fileService = inject(FileService);
  private destroyRef = inject(DestroyRef);

  isLoading = 0;
  isExporting = 0;
  hasPerimeter = false;
  gridApi?: GridApi;
  gridOptions!: GridOptions;
  gridReadyState$ = new BehaviorSubject<boolean>(false);
  gridReady$: Observable<boolean> = this.gridReadyState$.pipe(filter((ready) => ready));
  datasourceChanges$ = new Subject<void>();
  showTable = false;
  kpis: Kpi[] = [];
  kpiSearchFunction: (search: string, option: Kpi) => boolean = (search, option) => Str.search(search, option.label);
  selectedKpis: string[] = [];
  getSelectedKpis: () => Kpi[] = () => this.kpis.filter((kpi) => this.selectedKpis.includes(kpi.id));
  period$: Observable<Period | null>;
  max: Date | null;
  min: Date | null;

  granularities: TimeUnit[] = ['day', 'week', 'month'];
  disabledGranularities: DisabledGranularity[] = [];
  isGranularityDisabled = (granularity: TimeUnit) =>
    this.disabledGranularities.some(
      (disabled) => disabled === granularity || (typeof disabled === 'object' && disabled?.granularity === granularity)
    );
  undisplayableSelectedKpisMessage: string | null = null;
  noneOfSelectedKpisAreDisplayableDisclaimer: string | null = null;
  shortcuts: TimeShortcut[] = [
    'yesterday',
    'the_day_before_yesterday',
    'this_week',
    'last_week',
    'this_month',
    'last_month',
  ];
  disabledShortcuts: TimeShortcut[] = [];
  readonly PREFERENCES_KEY = 'supervision_kpi_table';
  readonly FAVORITES_KEY = 'supervision_kpi_table_favorites';
  favorites: TableFavorite[] = [];

  constructor() {
    this.period$ = this.periodService.period$;
    this.min = this.periodService.minDate;
    this.max = this.periodService.maxDate;
  }

  ngOnInit() {
    this.initTable();
    this.initFavorites();
    this.updateKpisAndTableOnFilterChange();
    this.updateDataSourceOnDateChange();
  }

  updateKpisAndTableOnFilterChange() {
    combineLatest([this.filterService.filter$, this.transloco.langChanges$])
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        this.routing.pauseWhileDetached(this.ref, this.destroyRef),
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
        tap(() => (this.hasPerimeter = this.filterService.hasPerimeter())),
        filter(() => this.hasPerimeter),
        tap(() => (this.kpis = [])),
        switchMap(() =>
          this.api
            .fetchContractKpis(
              this.filterService.getContract() as string,
              this.filterService.getTechno() as string,
              true
            )
            .pipe(
              takeUntilDestroyed(this.destroyRef),
              tap((kpis) => {
                const showRawKpis = this.userService.getShowRawKpis();
                this.kpis = kpis
                  .filter((kpi) => !kpi.admin_birdz_display || this.userService.isBirdzAdmin())
                  .filter((kpi) => !kpi.raw_display || showRawKpis)
                  .sort(Str.sortBy('label'));
                this.selectedKpis = kpis.map((kpi) => kpi.id);
                this.showTable = this.selectedKpis.length > 0;
                this.updateGranularities();
                this.periodService.setDefaultView();
                this.updateSelectedKpisAlerts();
              }),
              filter((kpis) => kpis.length > 0),
              tap(() => this.updateColumns()),
              tap(() => this.updateDataSource()),
              this.messenger.showFriendlyErrorMessage(EMPTY)
            )
        )
      )
      .subscribe();
  }

  updateDataSourceOnDateChange() {
    combineLatest([this.gridReady$, this.period$])
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        this.routing.pauseWhileDetached(this.ref, this.destroyRef),
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
        tap(() => (this.hasPerimeter = this.filterService.hasPerimeter())),
        filter(() => this.hasPerimeter),
        tap(() => this.updateSelectedKpisAlerts()),
        tap(() => this.updateColumns()),
        tap(() => this.updateDataSource()),
        this.messenger.showFriendlyErrorMessage(EMPTY)
      )
      .subscribe();
  }

  updateDataSource() {
    this.datasourceChanges$.next();
    if (!this.hasPerimeter || this.selectedKpis.length === 0 || this.noneOfSelectedKpisAreDisplayableDisclaimer) {
      this.gridApi!.setGridOption('datasource', {
        getRows: (params: IGetRowsParams) => {
          params.successCallback([], 0);
        },
      });
    } else {
      this.gridApi!.setGridOption('datasource', {
        getRows: (params: IGetRowsParams) => {
          this.gridApi!.setGridOption('loading', true);
          const request: PdsRequest = {
            sort: getSort(params),
            page: getPage(params),
            ...this.serializeFilters(params.filterModel),
          };
          this.api
            .fetchPdsIndex(this.filterService.toParams(), request)
            .pipe(
              this.messenger.showFriendlyErrorMessage(),
              takeUntil(this.datasourceChanges$.pipe(tap(() => params.failCallback()))),
              catchError((e) => {
                this.messenger.error(e.friendlyMessage);
                params.failCallback();
                return throwError(() => e);
              }),
              tap((result) => {
                params.successCallback(result.data, result.meta.total);
              }),
              finalize(() => {
                this.gridApi!.setGridOption('loading', false);
              })
            )
            .subscribe();
        },
      });
    }
  }

  updateGranularities() {
    // disable granularities for which there are no displayable KPIs
    this.disabledGranularities = this.granularities
      .filter((granularity) => {
        return (
          this.getSelectedKpis().length > 0 &&
          this.getSelectedKpis().every((kpi) => !isKpiDisplayableForGranularity(kpi, granularity))
        );
      })
      .map((granularity) => {
        const tooltip = __('NONE_OF_SELECTED_KPIS_ARE_DISPLAYABLE.' + granularity.toUpperCase());
        return { granularity, tooltip };
      });
  }

  updateSelectedKpisAlerts() {
    this.noneOfSelectedKpisAreDisplayableDisclaimer = null;
    this.undisplayableSelectedKpisMessage = null;

    const granularity = this.periodService.granularity;
    if (this.isGranularityDisabled(granularity)) {
      // If none of selected KPIs are displayable for the current granularity
      this.noneOfSelectedKpisAreDisplayableDisclaimer = __(
        'NONE_OF_SELECTED_KPIS_ARE_DISPLAYABLE.' + granularity.toUpperCase()
      );
    } else {
      const undisplayableSelectedKpis = this.getSelectedKpis()
        .filter((kpi) => !isKpiDisplayableForGranularity(kpi, granularity))
        .map((kpi) => kpi.label);

      // If some of them are not displayable
      if (undisplayableSelectedKpis.length > 0) {
        const warning = __('NOT_ALL_SELECTED_KPIS_ARE_DISPLAYABLE.' + granularity.toUpperCase());
        const kpis = undisplayableSelectedKpis.join(', ');
        this.undisplayableSelectedKpisMessage = `${warning} (${kpis}).`;
      }
    }
  }

  onGridReady(params: GridReadyEvent<any>) {
    this.gridApi = params.api;
    this.gridReadyState$.next(true);
  }

  serializeFilters(tableFilter: PdsFilterModel | null = null): PdsRequest {
    tableFilter ??= this.gridApi!.getFilterModel() ?? {};

    const kpiFilters: kpiFilters = {};

    // for each kpi gather filter values
    this.kpis.forEach((kpi) => {
      // numeric kpis (YTD, kapta daily rates) have a {min, max} range filter, the others a list of states
      if (hasNumericValues(kpi) && tableFilter[kpi.id]?.value) {
        kpiFilters[kpi.id] = tableFilter[kpi.id].value;
      } else {
        const values: KpiValue[] = tableFilter[kpi.id]?.value ?? [];
        if (values.length > 0) {
          kpiFilters[kpi.id] = values;
        }
      }
    });

    (INTERRUPTION_KPIS[this.filterService.getTechno() ?? ''] ?? []).forEach((interruptionKpi) => {
      if (tableFilter[interruptionKpi]?.value != null) {
        kpiFilters[interruptionKpi] = tableFilter[interruptionKpi].value;
      }
    });

    return {
      date: format(this.periodService.from ?? new Date(), 'yyyy-MM-dd'),
      granularity: this.periodService.granularity,
      date_derniere_trame_recue_min: tableFilter.date_derniere_trame_recue?.value?.from
        ? format(tableFilter.date_derniere_trame_recue?.value?.from, 'yyyy-MM-dd')
        : null,
      date_derniere_trame_recue_max: tableFilter.date_derniere_trame_recue?.value?.to
        ? format(tableFilter.date_derniere_trame_recue?.value?.to, 'yyyy-MM-dd')
        : null,
      etat_supervision: tableFilter.etat_supervision?.value?.map((value: string) => (value === 'NULL' ? null : value)),
      dateref_index_livre_min: tableFilter.dateref_index_livre?.value?.from
        ? format(tableFilter.dateref_index_livre?.value?.from, 'yyyy-MM-dd')
        : null,
      dateref_index_livre_max: tableFilter.dateref_index_livre?.value?.to
        ? format(tableFilter.dateref_index_livre?.value?.to, 'yyyy-MM-dd')
        : null,
      dateref_index_integre_min: tableFilter.dateref_index_integre?.value?.from
        ? format(tableFilter.dateref_index_integre?.value?.from, 'yyyy-MM-dd')
        : null,
      dateref_index_integre_max: tableFilter.dateref_index_integre?.value?.to
        ? format(tableFilter.dateref_index_integre?.value?.to, 'yyyy-MM-dd')
        : null,
      date_livraison_min: tableFilter.date_livraison?.value?.from
        ? format(tableFilter.date_livraison?.value?.from, 'yyyy-MM-dd')
        : null,
      date_livraison_max: tableFilter.date_livraison?.value?.to
        ? format(tableFilter.date_livraison?.value?.to, 'yyyy-MM-dd')
        : null,
      date_envoi_mesure_min: tableFilter.date_envoi_mesure?.value?.from
        ? format(tableFilter.date_envoi_mesure?.value?.from, 'yyyy-MM-dd')
        : null,
      date_envoi_mesure_max: tableFilter.date_envoi_mesure?.value?.to
        ? format(tableFilter.date_envoi_mesure?.value?.to, 'yyyy-MM-dd')
        : null,
      id_client: tableFilter.id_client?.value,
      id: tableFilter.id?.value,
      compteur: tableFilter.compteur?.value,
      module: tableFilter.module?.value,
      type_de_module: tableFilter.type_de_module?.value,
      version_de_module: tableFilter.version_de_module?.value,
      radio_mode: tableFilter.radio_mode?.value,
      raccorde: tableFilter.raccorde?.value,
      intensite_bruit_min: tableFilter.intensite_bruit?.value?.min,
      intensite_bruit_max: tableFilter.intensite_bruit?.value?.max,
      kpis: kpiFilters,
    };
  }

  initTable() {
    this.gridOptions = this.table.getDefaultGridOptions('infinite', {
      gridId: 'pds-index',
    });
  }

  getDeviceTypeOptions(): Option<string>[] {
    return ['G1 v1', 'G1 v2', 'G2 v1', 'G2 v2', 'G3 v1', 'G3 v2', 'G3 v3', 'G3 v4', 'G3 v5', 'SM4.1', 'SM4.2']
      .map((option) => ({ label: option, value: option }))
      .concat([{ label: __('OTHERS'), value: 'others' }]);
  }

  getRadioModeOptions(): Option<string>[] {
    return [
      { label: __('RADIO_MODE_HR'), value: 'HR' },
      { label: __('RADIO_MODE_CORONIS'), value: 'CORONIS' },
      { label: __('RADIO_MODE_LORAWAN'), value: 'LORAWAN' },
      { label: __('RADIO_MODE_SIGFOX'), value: 'SIGFOX' },
      { label: __('RADIO_MODE_EX'), value: 'EX' },
      { label: __('RADIO_MODE_WMBUS'), value: 'WMBUS' },
    ];
  }

  getKpiOptions(isRaw = false): Option<string>[] {
    const options: Option<string>[] = [
      { label: __('KPI_OK'), value: 'OK' },
      { label: __('KPI_KO'), value: 'KO' },
    ];
    if (!isRaw) {
      options.push({ label: __('KPI_EXCLUDED'), value: 'EXCLUDED' });
    }
    return options;
  }

  getConnectionOptions(): Option<number>[] {
    return [
      { label: __('OPTION_CONNECTED'), value: 1 },
      { label: __('OPTION_NOT_CONNECTED'), value: 0 },
    ];
  }

  getStateOptions(): Option<string>[] {
    return [
      { label: __('FUNCTIONAL'), value: 'OK' },
      { label: __('PRESUMED_NON_COMMUNICATING'), value: 'NC' },
      { label: __('PRESUMED_OUT_OF_SERVICE'), value: 'HS' },
      { label: __('UNKNOWN'), value: 'NULL' },
    ];
  }

  getColumns(applyUserState = true): ColDef[] {
    const allColumns: (ColDef | undefined)[] = [
      {
        colId: 'id_client',
        field: 'id_client',
        headerName: 'COL_ID_CLIENT',
        flex: 2,
        minWidth: 150,
        hide: false,
        filter: birdzTableFilterText,
        sortable: true,
      },
      this.userService.isBirdzAdmin()
        ? {
            colId: 'id',
            field: 'id',
            headerName: 'COL_ID',
            flex: 2,
            minWidth: 150,
            hide: false,
            type: ['birdzAdmin'],
            filter: birdzTableFilterText,
            sortable: true,
          }
        : undefined,
      {
        colId: 'compteur',
        field: 'compteur',
        headerName:
          ({ sewerin: 'COL_SEWERIN', kapta: 'COL_KAPTA' } as Record<string, string>)[
            this.filterService.getTechno() ?? ''
          ] ?? 'COL_COMPTEUR',
        flex: 2,
        minWidth: 150,
        hide: false,
        filter: birdzTableFilterText,
        sortable: true,
      },
      {
        colId: 'module',
        field: 'module',
        headerName: 'COL_MODULE',
        flex: 2,
        minWidth: 150,
        hide: false,
        filter: birdzTableFilterText,
        sortable: true,
        tooltipValueGetter: (params: ITooltipParams) => {
          return params.data?.chgmt_module ? __('MODULE_REPLACED') : '';
        },
        cellStyle: (params: CellClassParams) => {
          return params.data && params.data.chgmt_module ? { color: orange, fontWeight: 'bold' } : null;
        },
      },
      {
        colId: 'nom_commune',
        field: 'nom_commune',
        headerName: 'COL_NOM_COMMUNE',
        flex: 2,
        minWidth: 100,
        hide: false,
      },
      this.filterService.getTechno() === 'watermetering'
        ? {
            colId: 'type_de_module',
            field: 'type_de_module',
            headerName: 'COL_TYPE_DE_MODULE',
            flex: 2,
            minWidth: 100,
            hide: false,
            filter: birdzTableFilterSelect,
            filterParams: {
              multiple: true,
              options: this.getDeviceTypeOptions(),
            },
            sortable: true,
          }
        : undefined,
      this.userService.isBirdzAdmin()
        ? {
            colId: 'version_de_module',
            field: 'version_de_module',
            headerName: 'COL_VERSION_DE_MODULE',
            flex: 1,
            minWidth: 100,
            hide: false,
            type: ['birdzAdmin'],
            filter: birdzTableFilterText,
            sortable: true,
          }
        : undefined,
      {
        colId: 'radio_mode',
        field: 'radio_mode',
        headerName: 'COL_RADIO_MODE',
        flex: 1,
        minWidth: 80,
        hide: false,
        valueFormatter: valueFormatter({
          default: (params: ValueFormatterParams) => {
            const key = 'RADIO_MODE_' + params.value;
            const translation = __(key);

            return translation !== key ? translation : __('RADIO_MODE_UNKNOWN');
          },
        }),
        filter: birdzTableFilterSelect,
        filterParams: {
          multiple: true,
          options: this.getRadioModeOptions(),
        },
        sortable: true,
      },
      this.filterService.getTechno() === 'watermetering'
        ? {
            colId: 'raccorde',
            field: 'raccorde',
            headerName: 'COL_RACCORDE',
            flex: 2,
            minWidth: 100,
            hide: false,
            tooltipValueGetter: () => undefined,
            cellRendererSelector: (params: ICellRendererParams): CellRendererSelectorResult | undefined => {
              return params.value != null
                ? {
                    component: CellConnectionComponent,
                  }
                : undefined;
            },
            type: ['birdzCenter'],
            filter: birdzTableFilterSelect,
            filterParams: {
              multiple: true,
              options: this.getConnectionOptions(),
            },
            sortable: true,
          }
        : undefined,
      {
        colId: 'date_derniere_trame_recue',
        field: 'date_derniere_trame_recue',
        headerName: 'COL_DATE_DERNIERE_TRAME_RECUE',
        flex: 2,
        minWidth: 125,
        hide: false,
        type: ['birdzDate'],
        filter: birdzTableFilterDateRange,
        filterParams: {
          max: new Date(),
        },
        sortable: true,
      },
      this.filterService.getTechno() === 'watermetering'
        ? {
            colId: 'etat_supervision',
            field: 'etat_supervision',
            headerName: 'COL_ETAT_SUPERVISION',
            flex: 2,
            minWidth: 125,
            hide: false,
            tooltipValueGetter: () => undefined,
            cellRendererSelector: (params: ICellRendererParams): CellRendererSelectorResult | undefined => {
              return params.value
                ? {
                    component: CellStateComponent,
                  }
                : undefined;
            },
            headerComponentParams: {
              info: {
                component: ColumnHelpStateComponent,
              },
            },
            type: ['birdzCenter'],
            filter: birdzTableFilterSelect,
            filterParams: {
              multiple: true,
              options: this.getStateOptions(),
            },
            sortable: true,
          }
        : undefined,
      this.filterService.getTechno() === 'watermetering'
        ? {
            colId: 'dernier_index_livre',
            field: 'dernier_index_livre',
            headerName: 'COL_DERNIER_INDEX_LIVRE',
            flex: 2,
            minWidth: 125,
            hide: false,
            type: ['birdzInteger'],
          }
        : undefined,
      this.filterService.getTechno() === 'watermetering'
        ? {
            colId: 'dateref_index_livre',
            field: 'dateref_index_livre',
            headerName: 'COL_DATEREF_INDEX_LIVRE',
            flex: 2,
            minWidth: 125,
            hide: false,
            type: ['birdzDate'],
            filter: birdzTableFilterDateRange,
            filterParams: {
              max: new Date(),
            },
            sortable: true,
          }
        : undefined,
      // no delivery date yet for kapta
      this.filterService.getTechno() !== 'kapta'
        ? {
            colId: this.filterService.getTechno() === 'sewerin' ? 'date_envoi_mesure' : 'date_livraison',
            field: this.filterService.getTechno() === 'sewerin' ? 'date_envoi_mesure' : 'date_livraison',
            headerName: 'COL_DATE_LIVRAISON',
            flex: 2,
            minWidth: 125,
            hide: false,
            type: ['birdzDate'],
            filter: birdzTableFilterDateRange,
            filterParams: {
              max: new Date(),
            },
            sortable: true,
          }
        : undefined,
      ...this.getKpiColumns(),
      {
        colId: 'motif_exclu',
        field: 'motif_exclu',
        headerName: 'COL_MOTIF_EXCLU',
        flex: 2,
        minWidth: 125,
        hide: false,
      },
      this.userService.isBirdzAdmin() && this.filterService.getTechno() === 'watermetering'
        ? {
            colId: 'dernier_index_integre',
            field: 'dernier_index_integre',
            headerName: 'COL_DERNIER_INDEX_INTEGRE',
            flex: 2,
            minWidth: 125,
            hide: false,
            valueGetter: valueGetter((params) => {
              return params.data.dernier_index_integre ? params.data.dernier_index_integre / 1000 : null;
            }),
            context: {
              digitsInfo: '1.3-3',
              suffix: () => ' m³',
            },
            type: ['birdzFloat', 'birdzAdmin'],
          }
        : undefined,
      this.userService.isBirdzAdmin() && this.filterService.getTechno() === 'watermetering'
        ? {
            colId: 'dateref_index_integre',
            field: 'dateref_index_integre',
            headerName: 'COL_DATEREF_INDEX_INTEGRE',
            flex: 2,
            minWidth: 125,
            hide: false,
            type: ['birdzDate', 'birdzAdmin'],
            filter: birdzTableFilterDateRange,
            filterParams: {
              max: new Date(),
            },
            sortable: true,
          }
        : undefined,
      this.userService.isBirdzAdmin() && this.filterService.getTechno() === 'watermetering'
        ? {
            colId: 'type_service_export',
            field: 'type_service_export',
            headerName: 'COL_TYPE_SERVICE_EXPORT',
            flex: 2,
            minWidth: 125,
            hide: false,
            type: ['birdzAdmin'],
          }
        : undefined,
      this.userService.isBirdzAdmin() && this.filterService.getTechno() === 'watermetering'
        ? {
            colId: 'id_service_export',
            field: 'id_service_export',
            headerName: 'COL_ID_SERVICE_EXPORT',
            flex: 2,
            minWidth: 125,
            hide: false,
            type: ['birdzAdmin'],
          }
        : undefined,
      this.filterService.getTechno() === 'sewerin'
        ? {
            colId: 'intensite_bruit',
            field: 'intensite_bruit',
            headerName: 'COL_INTENSITE_BRUIT',
            flex: 2,
            minWidth: 100,
            hide: false,
            type: ['birdzInteger'],
            filter: birdzTableFilterRange,
            sortable: true,
          }
        : undefined,
    ];

    const columns = allColumns.filter((col) => col !== undefined);

    if (applyUserState) {
      applyStateToColDef(this.getSavedColumnState(), columns);
    }

    return columns;
  }

  getKpiColumns(): ColDef[] {
    const kpis = this.getSelectedKpis();
    const kpiColumns: ColDef[] = [];
    const granularity = this.periodService.granularity;
    let needsInterruptionInProgress = false;

    kpis.forEach((kpi) => {
      // If the kpi is not displayable for the current granularity, no column is added
      if (!isKpiDisplayableForGranularity(kpi, granularity)) {
        return;
      }

      if (kpi.ytd_display) {
        if (kpi.type === 'NB_JOURS') {
          needsInterruptionInProgress = true;
        }

        kpiColumns.push(
          this.getKpiColumnConfig(
            kpi.id,
            kpi.id,
            kpi.label,
            kpi.type,
            kpi.type === 'NB_JOURS' ? __('NB_DAYS_HELP', { kpi: kpi.label.replace(/\s*(YTD|AAJ)/g, '') }) : null
          )
        );
      } else {
        kpiColumns.push(
          this.getKpiColumnConfig(
            kpi.id,
            kpi.id,
            kpi.label + (kpi.daily_display && kpi.has_ytd_equivalent ? ` (${__('DAILY').toLowerCase()})` : ''),
            hasNumericValues(kpi) ? kpi.type : 'STATE'
          )
        );
      }
    });

    if (needsInterruptionInProgress) {
      (INTERRUPTION_KPIS[this.filterService.getTechno() ?? ''] ?? []).forEach((interruptionKpi) => {
        kpiColumns.push(
          this.getKpiColumnConfig(
            interruptionKpi,
            interruptionKpi,
            __(interruptionKpi),
            'NB_JOURS',
            __(`${interruptionKpi}_HELP`)
          )
        );
      });
    }

    return kpiColumns;
  }

  getKpiColumnConfig(id: string, field: string, label: string, type: string, help?: string | null): ColDef {
    const kpi = this.kpis.find((k) => k.id === id);
    const associatedDailyKpi = this.kpis.find((k) => k.id === id.replace('YTD_', ''));

    // set help info
    let headerComponentParams: { info?: unknown; tooltip?: string; translateTooltip?: boolean } = {};
    if (type === 'STATE') {
      headerComponentParams = {
        info: {
          component: ColumnHelpKpiComponent,
          componentParams: { techno: this.filterService.getTechno(), isRaw: kpi?.raw_display ?? false },
          title: __('HELP') + __('COLON') + label,
        },
      };
    } else if (help != null) {
      headerComponentParams = {
        info: {
          html: help,
          title: __('HELP') + __('COLON') + label,
        },
      };
    }

    // YTD rates: daily history of the associated daily kpi ; kpis with hourly data: their slots over the day
    const hasHourlyHistory = !!kpi && !kpi.ytd_display && kpi.hourly_display;
    const kpiWithViewableHistory = hasHourlyHistory
      ? kpi
      : associatedDailyKpi && kpi?.ytd_display && kpi.technology !== 'WATERMETERING' && kpi.type === 'RATIO'
        ? associatedDailyKpi
        : undefined;
    const hasViewableHistory = kpiWithViewableHistory !== undefined;

    // l'acronyme AAJ/YTD n'étant pas évident, on explicite sa signification dans le tooltip de la colonne
    // même chose pour les KPIs bruts
    if (kpi?.ytd_display) {
      headerComponentParams.tooltip = `${label} (${__('YTD_DEFINITION')})`;
    } else if (kpi?.raw_display) {
      headerComponentParams.tooltip = `${label} (${__('RAW_DEFINITION')})`;
    }

    return {
      headerName: label + (type === 'NB_JOURS' ? ` (${__('DAYS').toLowerCase()})` : ''),
      colId: id,
      field: id,
      tooltipValueGetter: () => undefined,
      // only cells with a value are clickable (no value, no history to display)
      cellStyle: (params: CellClassParams) =>
        hasViewableHistory && params.data?.kpis?.[field] != null ? { cursor: 'pointer' } : null,
      // the whole cell is clickable: the renderer fills it so that its hint covers it all
      cellClass: (params: CellClassParams) =>
        hasViewableHistory && params.data?.kpis?.[field] != null ? 'birdz-ag-full-size' : undefined,
      cellRendererSelector: (params: ICellRendererParams) => {
        const kpis = params.data?.kpis ?? [];
        const kpiValue = kpis[field];
        return kpiValue != null
          ? type === 'STATE'
            ? {
                component: CellKpiStateComponent,
                params: {
                  kpiValue: kpiValue,
                },
              }
            : {
                component: CellKpiNumberComponent,
                params: {
                  kpiType: type,
                  kpiValue: kpiValue,
                  historyHint: hasHourlyHistory
                    ? { key: 'KPI_DETAILS.PDS_KPI_HISTORY.SEE_HOURLY', params: { period: kpi?.hourly_period } }
                    : hasViewableHistory
                      ? { key: 'KPI_DETAILS.PDS_KPI_HISTORY.SEE_DAILY' }
                      : undefined,
                },
              }
          : undefined;
      },
      type: [type === 'STATE' ? 'birdzCenter' : 'birdzRight'],
      headerComponentParams: headerComponentParams,
      context: {
        translateHeaderName: true,
        hasViewableHistory: hasViewableHistory,
        hasHourlyHistory: hasHourlyHistory,
        kpi: kpiWithViewableHistory,
      },
      filter: type === 'STATE' ? birdzTableFilterSelect : birdzTableFilterRange,
      filterParams:
        type === 'STATE'
          ? {
              multiple: true,
              options: this.getKpiOptions(kpi?.raw_display ?? false),
            }
          : {
              minLimit: 0,
              maxLimit: type === 'NB_JOURS' ? 365 : 100,
            },
      sortable: true,
      sortingOrder: ['desc', 'asc', null],
    };
  }

  updateColumns() {
    if (this.gridApi) {
      this.gridApi.setGridOption('columnDefs', this.getColumns());
      this.gridApi.refreshHeader();
    }
  }

  resetColumns() {
    if (this.gridApi) {
      this.gridApi.setGridOption('columnDefs', this.getColumns(false));
      this.gridApi.refreshHeader();
    }
  }

  getSavedColumnState(): ColumnState[] {
    return (
      this.userService.getUser()?.preferences?.[this.PREFERENCES_KEY]?.[this.filterService.getTechno() ?? 'null'] ?? []
    );
  }

  onColumnsChanged(event: ColumnsChangedEvent) {
    if ([Initiator.Rearranger, Initiator.Favorite].includes(event.initiator)) {
      if (event.columns === null) {
        this.resetColumns();
      }
      this.savePreferences(event.columns);
    }
  }

  onKpiSelectionChanged() {
    this.updateGranularities();
    this.updateSelectedKpisAlerts();
    this.showTable = this.selectedKpis.length > 0;
    if (this.showTable) {
      this.updateColumns();
      this.updateDataSource();
    }
  }

  onCellClicked(event: CellClickedEvent) {
    // only cells with a value are clickable (no value, no history to display)
    const kpiValue = event.colDef.field ? event.data?.kpis?.[event.colDef.field] : null;
    if (!event.colDef.context.hasViewableHistory || kpiValue == null) {
      return;
    }

    const kpi = event.colDef.context.kpi;
    if (!kpi) {
      return;
    }

    const pdsId = event.data?.id;
    const pdsCustomerId = event.data?.id_client;
    if (!pdsId) {
      return;
    }

    if (event.colDef.context.hasHourlyHistory) {
      this.openHourlyHistory(kpi, pdsId, pdsCustomerId);
      return;
    }

    // every day from January 1st to the selected date
    const to = this.getSelectedDay();
    const from = new Date(to.getFullYear(), 0, 1);

    this.dialog.open(ModalKpiPdsHistoryComponent, {
      width: '1000px',
      maxWidth: '95vw',
      data: {
        pdsCustomerId,
        kpi: kpi.id,
        kpiLabel: kpi.label,
        kpiType: kpi.type,
        // numeric daily kpis (kapta rates per pds) or states (sewerin)
        display: hasNumericValues(kpi) ? 'rate' : 'state',
        pdsId,
        from,
        to,
      },
    });
  }

  /**
   * Last day of the selected period (the selected day in 'day' mode), at most today
   */
  getSelectedDay(): Date {
    const today = new Date();
    return this.periodService.to && this.periodService.to < today ? this.periodService.to : today;
  }

  /**
   * Opens the OK/KO state of every slot of the day, for the last day of the selected period
   */
  openHourlyHistory(kpi: Kpi, pdsId: string, pdsCustomerId: string) {
    const day = this.getSelectedDay();

    this.dialog.open(ModalKpiPdsHistoryComponent, {
      width: '1000px',
      maxWidth: '95vw',
      data: {
        pdsCustomerId,
        kpi: kpi.id,
        kpiLabel: `${kpi.label} ${kpi.hourly_period}min - ${this.formatter.date(day)}`,
        kpiType: kpi.type,
        display: 'state',
        granularity: 'hour',
        hourlyPeriod: kpi.hourly_period,
        pdsId,
        from: day,
        to: day,
      },
    });
  }

  openContractKpiHelp() {
    this.dialog.open(ModalHelpContractConfigComponent, {
      maxWidth: '900px',
      data: {
        kpis: this.kpis,
        techno: this.filterService.getTechno(),
      },
    });
  }

  export() {
    const displayedColumns = this.gridApi!.getAllDisplayedColumns();

    if (!displayedColumns || displayedColumns.length === 0) {
      this.messenger.error(__('NO_DATA_TO_EXPORT'));
      return;
    }

    const selectedColumns = displayedColumns.map((column: Column) => column.getId());

    const columnState = this.gridApi!.getColumnState();
    const sortedColumn = columnState.find((col) => col.sort); // get first sorted column
    const sort = sortedColumn ? (sortedColumn?.sort === 'desc' ? '-' : '') + sortedColumn?.colId : undefined;

    const request: PdsExportRequest = {
      ...this.serializeFilters(this.gridApi!.getFilterModel()),
      selectedColumns: selectedColumns,
      sort: sort,
    };

    this.isExporting++;
    this.api
      .exportPdsIndex(this.filterService.toParams(), request)
      .pipe(
        tap((response) => this.fileService.downloadURL(response.public_url, response.filename, '_self')), // target '_self' to avoid pop-up behavior (chrome blocks this permission by default and prevents the download from happening)
        this.messenger.showFriendlyErrorMessage(EMPTY),
        finalize(() => this.isExporting--)
      )
      .subscribe();
  }

  saveFavoriteFn = (label: string, columns: ColumnState[]) => {
    const favorite = { id: label, label, columns };
    return this.saveFavorites$([...this.favorites, favorite]);
  };

  saveFavorites$(favorites: TableFavorite[]) {
    return this.userService
      .patchPreferences({
        [this.FAVORITES_KEY]: favorites,
      })
      .pipe(
        tap(
          (preferences) =>
            (this.favorites = ((preferences as Record<string, unknown>)?.[this.FAVORITES_KEY] ?? []) as TableFavorite[])
        ),
        this.messenger.showFriendlyErrorMessage()
      );
  }

  updateFavoriteFn = (fav: TableFavorite) => {
    this.replaceFavorite(fav);
    return this.saveFavorites$(this.favorites);
  };

  deleteFavoriteFn = (id: number | string) => {
    const index = this.favorites.findIndex((favorite) => favorite.id === id);
    if (index > -1) {
      const favorites = this.favorites.slice();
      favorites.splice(index, 1);
      return this.saveFavorites$(favorites);
    } else {
      return throwError(() => this.messenger.error(__('FAVORITE_NOT_FOUND')));
    }
  };

  replaceFavorite(favorite: TableFavorite) {
    const index = this.favorites.findIndex((f) => favorite.id === f.id);
    if (index > -1) {
      this.favorites[index] = favorite;
      this.favorites = this.favorites.slice();
    }
  }

  initFavorites() {
    this.favorites = (this.userService.getUser()?.preferences?.[this.FAVORITES_KEY] ?? []).filter(isTableFavorite);
  }

  savePreferences(preferences: ColumnState[] | null) {
    // if we're setting a columnState preference, remember the kpi columns from the previous state and insert them after the current ones
    if (preferences !== null) {
      const keptKpiState = this.getSavedColumnState().filter(
        (column) =>
          (column.colId.startsWith('OK_') || column.colId.startsWith('RAW_') || column.colId.startsWith('YTD_')) && // is kpi column
          !this.selectedKpis.includes(column.colId) // is not already in the current state
      );

      if (keptKpiState.length > 0) {
        // Find last KPI index in current column state
        let lastKpiIndex = -1; // default value meaning not found
        for (let i = preferences.length - 1; i >= 0; i--) {
          if (this.selectedKpis.includes(preferences[i].colId)) {
            lastKpiIndex = i;
            break;
          }
        }

        // Insert after last KPI
        if (lastKpiIndex !== -1) {
          preferences.splice(lastKpiIndex + 1, 0, ...keptKpiState); // insert after the last current kpi
        } else {
          preferences.push(...keptKpiState); // insert at the end
        }
      }
    }

    this.userService
      .patchPreferences({
        [this.PREFERENCES_KEY]: { [this.filterService.getTechno() ?? 'null']: preferences },
      })
      .pipe(
        tap(() => this.messenger.success(__(preferences === null ? 'PREFERENCES_RESET' : 'PREFERENCES_UPDATED'))),
        this.messenger.showFriendlyErrorMessage()
      )
      .subscribe();
  }
}
